import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { KeyRound, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ApiError } from '@/lib/api'
import { authApi } from '@/features/auth/api'
import { PasswordStrength, isPasswordStrong } from '@/components/PasswordStrength'

const errMsg = (err: unknown, fallback = 'Reset failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const isStaff = params.get('staff') === '1'
  const signInPath = isStaff ? '/admin-login' : '/login'
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!token) {
      setError('Reset token missing or invalid.')
      return
    }
    if (!isPasswordStrong(password)) {
      setError('Password does not meet all the requirements listed below.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setBusy(true)
    try {
      const r = await authApi.resetPassword({ token, password })
      setSuccess(r.message ?? 'Password reset. Redirecting to sign in…')
      setTimeout(() => navigate(signInPath), 2000)
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center text-2xl">Set a new password</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {success ? (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        ) : null}
        <form onSubmit={submit} className="space-y-3">
          <FormField
            label="New password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            autoComplete="new-password"
          />
          <PasswordStrength password={password} />
          <FormField
            label="Confirm password"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            autoComplete="new-password"
            error={
              confirm && confirm !== password ? 'Passwords do not match.' : undefined
            }
          />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
            Reset password
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground">
          <Link to={signInPath} className="text-primary hover:underline">
            {isStaff ? '← Back to staff sign in' : '← Back to sign in'}
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
